/**
 * Liste des pays et reconnaissance d'un libellé libre.
 *
 * <p>Vit ici et non dans {@code shared-util}, qui importe déjà {@code shared-map-libre} : l'y
 * mettre créerait un cycle entre bibliothèques. Son consommateur principal est de toute façon le
 * registre satellite ; le formulaire d'adresse s'en sert pour proposer la même liste, de sorte
 * qu'il n'existe qu'une seule table de correspondance.
 */

/** ISO 3166-1 alpha-2. Les libellés sont produits par le navigateur, pas recopiés ici. */
export const COUNTRY_CODES: readonly string[] = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AL',
  'AM',
  'AO',
  'AR',
  'AT',
  'AU',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BN',
  'BO',
  'BR',
  'BS',
  'BT',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FM',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GH',
  'GM',
  'GN',
  'GQ',
  'GR',
  'GT',
  'GW',
  'GY',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IN',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MR',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NE',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PG',
  'PH',
  'PK',
  'PL',
  'PT',
  'PW',
  'PY',
  'QA',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SI',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SY',
  'SZ',
  'TD',
  'TG',
  'TH',
  'TJ',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TZ',
  'UA',
  'UG',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VN',
  'VU',
  'WS',
  'YE',
  'ZA',
  'ZM',
  'ZW',
];

export interface CountryOption {
  code: string;
  label: string;
}

/** Nom d'un pays dans une langue, via le navigateur — aucune liste de noms à maintenir. */
function displayName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    // Environnement sans Intl complet : le code reste lisible.
    return code;
  }
}

/** La liste à proposer, triée sur le libellé affiché. */
export function countryOptions(locale = 'fr'): CountryOption[] {
  return COUNTRY_CODES.map(code => ({ code, label: displayName(code, locale) })).sort((a, b) =>
    a.label.localeCompare(b.label, locale)
  );
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Index construit une fois : code, nom français et nom anglais pointent vers le même code. */
let index: Map<string, string> | null = null;

function lookup(): Map<string, string> {
  if (index) {
    return index;
  }
  index = new Map();
  for (const code of COUNTRY_CODES) {
    index.set(normalize(code), code);
    for (const locale of ['fr', 'en']) {
      const name = displayName(code, locale);
      if (name !== code) {
        index.set(normalize(name), code);
      }
    }
  }
  // Formes usuelles que les catalogues officiels ne rendent pas.
  index.set('czech republic', 'CZ');
  index.set('republique tcheque', 'CZ');
  index.set('ceska republika', 'CZ');
  index.set('cesko', 'CZ');
  index.set('republique francaise', 'FR');
  index.set('deutschland', 'DE');
  index.set('espana', 'ES');
  return index;
}

/**
 * Le code ISO derrière un libellé libre, ou {@code null} si on ne reconnaît rien.
 *
 * <p>{@code Address.country} est du texte saisi à la main : on accepte donc le code, le nom
 * français et le nom anglais, accents et casse indifférents.
 */
export function toCountryCode(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const needle = normalize(value);
  return needle ? (lookup().get(needle) ?? null) : null;
}
